import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { History, Refresh } from '@mui/icons-material';
import { queryAuditLog, type AuditLogFilters } from '../services/auditLogService';
import { EventDetailDialog } from '../components/EventDetailDialog';
import { SoftChip, type SoftChipTone } from '../components/SoftChip';
import type { MktEvent, MktEventType } from '@shared/types';

const EVENT_TYPES: MktEventType[] = [
  'tenant_addons_updated',
  'tenant_billing_overridden',
  'impersonation_started',
  'impersonation_ended',
  'impersonation_visited',
  'tenant_created',
  'tenant_suspended',
  'tenant_reactivated',
];

/** Tone per event_type — usato per dare un colpo d'occhio sui rischi:
 *  - impersonation = warning (azione privilegiata)
 *  - tenant_suspended/billing_overridden = error
 *  - tenant_created/reactivated = success
 *  - resto = primary/default */
const EVENT_TONE: Partial<Record<MktEventType, SoftChipTone>> = {
  impersonation_started: 'warning',
  impersonation_ended: 'default',
  impersonation_visited: 'info',
  tenant_suspended: 'error',
  tenant_billing_overridden: 'error',
  tenant_created: 'success',
  tenant_reactivated: 'success',
  tenant_addons_updated: 'primary',
};

function formatTimestamp(ts: MktEvent['timestamp']): string {
  if (typeof ts === 'string') return new Date(ts).toLocaleString('it-IT');
  if (ts && typeof ts === 'object' && 'seconds' in ts) {
    return new Date(ts.seconds * 1000).toLocaleString('it-IT');
  }
  return '—';
}

export const AuditLogPage: React.FC = () => {
  const theme = useTheme();
  const [events, setEvents] = useState<MktEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<MktEvent | null>(null);
  const [filters, setFilters] = useState<AuditLogFilters>({ event_type: 'all', max: 100 });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await queryAuditLog(filters);
      setEvents(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.event_type, filters.tenant_id]);

  return (
    <Box>
      {/* Header pagina */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
            <History sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>
              Audit log
            </Typography>
            <SoftChip label={`${events.length} eventi`} tone="default" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Stream globale eventi mkt_events. Ordinati per timestamp desc.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={load}
          disabled={loading}
          sx={{ flexShrink: 0 }}
        >
          Aggiorna
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filtri */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            label="Event type"
            value={filters.event_type ?? 'all'}
            onChange={(e) =>
              setFilters({ ...filters, event_type: e.target.value as MktEventType | 'all' })
            }
            size="small"
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="all">Tutti</MenuItem>
            {EVENT_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Tenant ID (filtro esatto)"
            value={filters.tenant_id ?? ''}
            onChange={(e) => setFilters({ ...filters, tenant_id: e.target.value || undefined })}
            size="small"
            sx={{ flex: 1 }}
            slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
          />
        </Stack>
      </Paper>

      {/* Tabella eventi */}
      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Event type</TableCell>
                <TableCell>Tenant ID</TableCell>
                <TableCell>Caller UID</TableCell>
                <TableCell align="right">Dettaglio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((ev) => (
                <TableRow key={ev.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                    {formatTimestamp(ev.timestamp)}
                  </TableCell>
                  <TableCell>
                    <SoftChip
                      label={ev.event_type}
                      tone={EVENT_TONE[ev.event_type] ?? 'default'}
                      mono
                    />
                  </TableCell>
                  <TableCell>
                    {ev.tenant_id
                      ? <SoftChip label={ev.tenant_id} tone="default" mono />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'text.secondary' }}>
                    {ev.caller_uid || '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => setSelected(ev)}>
                      JSON
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.disabled">Nessun evento</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <EventDetailDialog event={selected} onClose={() => setSelected(null)} />
    </Box>
  );
};
